/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2021, 2025
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

import {
  TabList,
  TabPanels,
  TabPanel,
  Tabs,
  Tab,
  TextArea
} from '@carbon/react';
import cx from 'classnames';
import { bool, string, func } from 'prop-types';
import React, { useState } from 'react';
import { useIntl } from 'react-intl';

import { ctaEvents } from '@ibm-watson/shared/modules/ia/util/trackers/segment';
import { messages } from './messages';

import './SearchResultDialogs.scss';

/**
 * Component for showing search result dialogs in tabs.
 * It configures success, no results found and connection error messages to show in the preview
 */
export const SearchResultDialogs = ({
  successMessage,
  setSuccessMessage,
  noResultsMessage,
  setNoResultsMessage,
  connectivityMessage,
  setConnectivityMessage,
  hideSuccessMessageTab,
  onlyShowDisabledSuccessMessageTab,
  readOnly
}) => {
  const { formatMessage: t } = useIntl();

  const [selectedTabIndex, setSelectedTabIndex] = useState(0);

  const disabled = onlyShowDisabledSuccessMessageTab;

  const onMessagesBlur = option => {
    ctaEvents[`searchMessageTab${option}`]();
  };

  const successMessageTab = {
    label: t(messages.successLabel),
    value: successMessage,
    tabId: 'SearchSkillConfig__tabs-results',
    id: 'SearchSkillConfig__message',
    onChangeFn: evt => setSuccessMessage(evt.target.value),
    onBlurFn: () => onMessagesBlur('Success')
  };

  const noResultsTab = {
    label: t(messages.noResultsLabel),
    tabId: 'SearchSkillConfig__tabs-no-results',
    value: noResultsMessage,
    id: 'SearchSkillConfig__noResultFound',
    onChangeFn: evt => setNoResultsMessage(evt.target.value),
    onBlurFn: () => onMessagesBlur('NoResults')
  };

  const connectivityIssueTab = {
    label: t(messages.connectivityIssueLabel),
    tabId: 'SearchSkillConfig__tabs-error',
    value: connectivityMessage,
    id: 'SearchSkillConfig__errorMessage',
    onChangeFn: evt => setConnectivityMessage(evt.target.value),
    onBlurFn: () => onMessagesBlur('Error')
  };

  const messageTabs = [];

  if (onlyShowDisabledSuccessMessageTab) {
    // If conversational search is enabled then there is a second copy of this component, that gets rendered below the
    // first, which is disabled with only the success message in it. We do this because the success message is not used
    // by conversational search.
    messageTabs.push(successMessageTab);
  } else if (hideSuccessMessageTab) {
    // If conversational search is enabled then the first copy of this component only includes the no results tab and
    // the connectivity issue tab, since the success message is not used by conversational search.
    messageTabs.push(noResultsTab);
    messageTabs.push(connectivityIssueTab);
  } else {
    // If conversational search is not enabled then we add all the message tabs.
    messageTabs.push(successMessageTab);
    messageTabs.push(noResultsTab);
    messageTabs.push(connectivityIssueTab);
  }

  return (
    <>
      <h4
        className={cx('SearchResultDialogs__title', {
          disabled
        })}>
        {t(messages.messagesAreaTitle)}
      </h4>
      <p className={cx('SearchResultDialogs__instruction', { disabled })}>
        {t(messages.messagesAreaInstructions)}
      </p>
      <Tabs
        selected={selectedTabIndex}
        className="SearchResultDialogs__tabs"
        onSelectionChange={i => setSelectedTabIndex(i)}
        tabContentClassName="SearchResultDialogs__messages">
        <TabList aria-label="List of tabs">
          {messageTabs.map(({ label, tabId }) => (
            <Tab id={tabId} key={tabId} disabled={disabled}>
              {label}
            </Tab>
          ))}
        </TabList>

        <TabPanels>
          {messageTabs.map(({ label, id, value, onChangeFn, onBlurFn }) => (
            <TabPanel key={id}>
              <TextArea
                labelText={label}
                hideLabel
                className="SearchResultDialogs__response"
                maxLength={1024}
                cols={150}
                placeholder={label}
                value={value}
                id={id}
                onChange={onChangeFn}
                onBlur={onBlurFn}
                disabled={disabled || readOnly}
              />
            </TabPanel>
          ))}
        </TabPanels>
      </Tabs>
    </>
  );
};

SearchResultDialogs.propTypes = {
  successMessage: string,
  setSuccessMessage: func,
  noResultsMessage: string,
  setNoResultsMessage: func,
  connectivityMessage: string,
  setConnectivityMessage: func,
  hideSuccessMessageTab: bool,
  onlyShowDisabledSuccessMessageTab: bool,
  readOnly: bool
};
